import DockPanel from "@pathwright/ui/src/components/panel/DockPanel"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Link from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useCallback, useRef, useState } from "react"
import { useHistory, useParams, useRouteMatch } from "react-router-dom"
import { DiscussionContextInput } from "@pathwright/graphql-client/src/generated"
import { getStepVerb } from "../../path/utils"
import DiscussionProvider, {
  DiscussionProviderProps,
  useDiscussionContext
} from "../DiscussionProvider"
import { useDiscussionEventHandlers } from "../list/DiscussionList"
import DiscussionItemContainer from "../list/item/DiscussionItemContainer"
import DiscussionPanel from "./DiscussionPanel"

// Shared props for all discussion dock panels.
const sharedDockPanelProps = {
  panelWidthPerc: 0.8
}

type DockPanelWithContextProps = {
  onClose?: () => void
  onWillClose?: () => void
  isOpen?: boolean
}

function DockPanelWithContext({
  onClose,
  onWillClose,
  isOpen
}: DockPanelWithContextProps) {
  const discussion = useDiscussionContext((c) => c.post)
  const { tc } = useTranslate()

  // TODO: looks like the step is possibly always the source path item, not the cohort path item (when you'd expect it).
  // This could lead to unexpected verb/name values.
  const titleProps = discussion?.step
    ? {
        title: `${getStepVerb(discussion.step.verb!, tc)}: ${
          discussion.step.name
        }`,
        link: `/library/${discussion.resource_id}/${discussion.cohort_id}/path/step/${discussion.step_id}/`
      }
    : {}

  return (
    <DockPanel
      {...titleProps}
      onClose={onClose}
      onWillClose={onWillClose}
      // To control open state from router.
      isOpen={isOpen}
      {...sharedDockPanelProps}
    >
      <DiscussionPanel />
    </DockPanel>
  )
}

type RoutedDiscussionDockPanelProps = {
  context: DiscussionContextInput
  // What to replace the matched URL with when closing the panel.
  replaceMatch?: string
  // Whether to allow creating new discussions from this panel.
  matchExistingDiscussion?: boolean
  // Whether to allow managing existing discussions from this panel.
  matchNewDiscussion?: boolean
}

// Router-connected DiscussionDockPanel.
export function RoutedDiscussionDockPanel({
  context,
  replaceMatch,
  matchExistingDiscussion = false,
  matchNewDiscussion = false
}: RoutedDiscussionDockPanelProps) {
  // Note that a -1 id indicates new discussion.
  // "Exiting" discussion ID – meaning the panel rendering this discussion is closing.
  const [exitingDiscussionId, setExitingDiscussionId] = useState<number | null>(
    null
  )

  // The discussion routes are children of the route rendering this panel.
  const params = useParams<{
    discussionId?: string
    discussionNew?: string
    responseId?: string
  }>()
  const { url } = useRouteMatch()
  const history = useHistory()

  const isExistingDiscussion = !!params.discussionId
  const isNewDiscussion = !!params.discussionNew

  const discussionId =
    isExistingDiscussion && matchExistingDiscussion
      ? Number(params.discussionId)
      : isNewDiscussion && matchNewDiscussion
      ? -1
      : null

  const responseId = params.responseId ? Number(params.responseId) : null

  const handleOnClose = useCallback(() => {
    setExitingDiscussionId(null)
  }, [])

  const handleWillClose = useCallback(() => {
    if (discussionId) setExitingDiscussionId(discussionId)

    if (isExistingDiscussion || isNewDiscussion) {
      // Remove /discussion/:discussionId* or /discussion/new* from URL
      history.push(
        url.replace(/\/discussion\/(\d+|new)\/?.*/, replaceMatch ?? "/")
      )
    }
  }, [discussionId, isExistingDiscussion, isNewDiscussion, url, replaceMatch])

  // Mainly for handling "delete" event existing discussions.
  useDiscussionEventHandlers({
    onDiscussionCreated: handleWillClose,
    onDiscussionDeleted: handleWillClose
  })

  const currentDiscussionId = discussionId || exitingDiscussionId

  return currentDiscussionId ? (
    currentDiscussionId === -1 ? (
      <DockPanel
        onClose={handleOnClose}
        onWillClose={handleWillClose}
        // Controlling open state, closing when user leaves new discussion route.
        isOpen={isNewDiscussion}
        {...sharedDockPanelProps}
      >
        <DiscussionItemContainer
          initialMode="create"
          context={context}
          // HACK: defaulting isDiscussionQuestion to true if no discussion
          // since we only ever at this point allow creating a "discussion question"
          // from the discussion panel.
          isDiscussionQuestion={true}
          compact={false}
          onCreateDiscussion={handleWillClose}
          onCancel={handleWillClose}
        />
      </DockPanel>
    ) : (
      <DiscussionDockPanel
        discussionId={currentDiscussionId}
        responseId={responseId}
        context={context}
        onClose={handleOnClose}
        onWillClose={handleWillClose}
        // Controlling open state, closing when user leaves existing discussion route.
        isOpen={isExistingDiscussion}
      />
    )
  ) : null
}

type DiscussionDockPanelProps = {
  discussionId: number
  responseId?: number | null
  context: DiscussionContextInput
} & Pick<
  DiscussionProviderProps,
  | "visuallyReverseResponses"
  | "orderResponsesBy"
  | "onViewResponse"
  | "onViewAllResponses"
  | "onDiscussionCreated"
  | "onDiscussionResponseCreated"
> &
  DockPanelWithContextProps

function DiscussionDockPanel({
  discussionId,
  responseId,
  context,
  visuallyReverseResponses,
  orderResponsesBy,
  onViewResponse,
  onViewAllResponses,
  onDiscussionCreated,
  onDiscussionResponseCreated: onDiscussionResponseCreatedProp,
  ...dockPanelProps
}: DiscussionDockPanelProps) {
  const toastIdRef = useRef<string>("")

  // Handle toasting when responses are created.
  const onDiscussionResponseCreated = useCallback<
    NonNullable<DiscussionProviderProps["onDiscussionResponseCreated"]>
  >(
    (discussion, response, responseParent) => {
      onDiscussionResponseCreatedProp?.(discussion, response, responseParent)
      // If a response was created, and we're not already viewing that response,
      toastIdRef.current = toaster.create({
        type: "success",
        duration: 3000,
        title: responseParent
          ? `Replied to ${responseParent.user?.display_name}`
          : "Response posted",
        description: (
          // Direct link to view the response.
          // UX might improve if we link to the parent response and ensure the sort order is
          // reset to "newest first" so the response is visible.
          <Link
            variant="underline"
            relativeTo={
              responseId ? `../${response.id}/` : `/response/${response.id}/`
            }
            onClick={() => toaster.dismiss(toastIdRef.current)}
            color="white"
            colorPalette="whiteAlpha"
          >
            View response
          </Link>
        )
      })
    },
    [responseId]
  )

  return (
    <DiscussionProvider
      id={discussionId}
      responseId={responseId}
      context={context}
      visuallyReverseResponses={visuallyReverseResponses}
      orderResponsesBy={orderResponsesBy}
      includeArchivedResponses
      onDiscussionResponseCreated={onDiscussionResponseCreated}
      onViewResponse={onViewResponse}
      onViewAllResponses={onViewAllResponses}
      onDiscussionCreated={onDiscussionCreated}
    >
      <DockPanelWithContext {...dockPanelProps} />
    </DiscussionProvider>
  )
}

export default DiscussionDockPanel
